"use client"

import { ArrowRightLeft, Wrench, Package, FlaskConical, Hexagon, CircleDashed } from "lucide-react"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ProcessStep } from "@/components/dashboard/ProcessStep"
import type { StationConfig } from "@/lib/mprc-config"
import type { MprcData } from "@/hooks/use-mprc-socket"
import { SIEMENS_BLUE } from "@/lib/mprc-config"

const ICONS = {
  conveyor: ArrowRightLeft,
  assembly: Wrench,
  packaging: Package,
  testing: FlaskConical,
  hexnut: Hexagon,
}

interface StationCardProps {
  station: StationConfig
  data: MprcData
}

export function StationCard({ station, data }: StationCardProps) {
  const Icon = ICONS[station.icon as keyof typeof ICONS] ?? Package

  const completedCount = station.steps.filter((step) => Boolean(data[step.nodeId])).length
  const totalCount = station.steps.length
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
  const allDone = completedCount === totalCount && totalCount > 0
  const inProgress = completedCount > 0 && !allDone

  if (!station.isActive) {
    return (
      <Card
        size="sm"
        className="bg-card/40 ring-foreground/5 border-dashed border-border/40 items-center justify-center min-h-[140px]"
      >
        <div className="flex flex-col items-center gap-2 px-4 py-4">
          <div
            className="size-9 rounded-lg flex items-center justify-center opacity-40"
            style={{ background: `${SIEMENS_BLUE}18` }}
          >
            <Icon className="size-4" style={{ color: SIEMENS_BLUE }} />
          </div>
          <h3 className="text-xs font-semibold text-muted-foreground/70 text-center leading-tight">
            {station.label}
          </h3>
          <Badge variant="outline" className="text-[9px] font-mono uppercase tracking-widest opacity-60">
            <CircleDashed className="size-2.5" />
            Coming Soon
          </Badge>
        </div>
      </Card>
    )
  }

  return (
    <Card
      size="sm"
      className="relative pt-0 gap-0 ring-foreground/10 hover:ring-foreground/20 transition-shadow min-h-0"
    >
      {/* Top accent strip */}
      <div className="h-[3px] rounded-t-xl" style={{ background: SIEMENS_BLUE }} />

      <CardHeader className="pt-3 pb-2.5 px-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="size-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${SIEMENS_BLUE}18`, border: `1.5px solid ${SIEMENS_BLUE}35` }}
          >
            <Icon className="size-4" style={{ color: SIEMENS_BLUE }} />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="text-sm leading-tight truncate">{station.label}</CardTitle>
            <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
              {completedCount} / {totalCount} steps
            </p>
          </div>
          <Badge
            variant={allDone ? "default" : inProgress ? "secondary" : "outline"}
            className={`text-[10px] font-mono shrink-0 ${
              allDone
                ? "bg-emerald-500/15 text-emerald-500 dark:text-emerald-400 hover:bg-emerald-500/15"
                : inProgress
                ? "bg-primary/15 text-primary hover:bg-primary/15"
                : "text-muted-foreground"
            }`}
          >
            {totalCount > 0 ? `${pct}%` : "—"}
          </Badge>
        </div>

        <Progress
          value={pct}
          className="h-1 mt-2.5"
        />
      </CardHeader>

      {/* Steps list — scrolls internally if it overflows allotted card height */}
      <CardContent className="flex-1 min-h-0 p-0 border-t border-border/30">
        <ScrollArea className="h-full">
          <div className="divide-y divide-border/25">
            {station.steps.map((step, index) => (
              <ProcessStep
                key={step.nodeId}
                stepNumber={index + 1}
                label={step.label}
                isActive={Boolean(data[step.nodeId])}
              />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
